export const EVENT_SOURCES = {
  HOVER: 'hover_event',
  CLICK: 'click_event',
  QA: 'qa_linkage_event',
};

export const STABLE_CHART_TYPES = [
  'bar_chart',
  'line_chart',
  'pie_chart',
  'scatter_plot',
  'histogram',
  'box_plot',
  'area_chart',
  'band_graph',
  'dot_plot',
  'venn_diagram',
  'tree_diagram',
  'linear_function',
  'quadratic_function',
  'exponential_function',
  'logarithmic_function',
  'trigonometric_function',
  'inverse_proportion',
  'proportion',
];

export const PRIMARY_ROLLOUT_CHARTS = [
  'bar_chart',
  'line_chart',
  'linear_function',
  'trigonometric_function',
];

export const REPLAY_ELIGIBLE_ACTION_TYPES = new Set([
  'focus_single_element',
  'highlight_elements',
  'dim_irrelevant',
  'show_value_tags',
  'attention_guide',
]);

const STATISTICAL_CHARTS = new Set([
  'bar_chart',
  'line_chart',
  'pie_chart',
  'scatter_plot',
  'histogram',
  'box_plot',
  'area_chart',
  'band_graph',
  'dot_plot',
  'venn_diagram',
  'tree_diagram',
]);

const FUNCTION_CHARTS = new Set([
  'linear_function',
  'quadratic_function',
  'exponential_function',
  'logarithmic_function',
  'trigonometric_function',
  'inverse_proportion',
  'proportion',
]);

const ACTION_ALIASES = {};

// D5 — the three actions that carry out what the STUDENT asked for: reveal
// something the stage is hiding, move to the stage they want to look at, or
// single out the one element they pointed at.
//
// These are ungated at EVERY support level (1-4), which is deliberate and
// different in kind from every other entry below. The rest of this policy
// rations AI-ADDED SCAFFOLDING — the tool deciding, on the student's behalf,
// that more decoration would help — and rationing it by support level is
// right, because clutter costs the most for exactly the students at the high
// levels. D5 is the opposite direction of travel: the student steers their own
// chart, and the tool obeys. Gating that by support level would take the
// steering wheel away from precisely the students who most need to be able to
// say "no, show me THAT part" — so it is not gated.
// (User ruling, 2026-07-26.)
const STUDENT_STEERING_ACTIONS = [
  'set_element_visibility',
  'set_chart_view',
  'focus_view_on_element',
];

const ACTION_ALLOWLIST = new Set([
  'highlight_elements',
  'dim_irrelevant',
  'focus_single_element',
  'bold_axes',
  'show_value_tags',
  'show_grid',
  'show_projection_lines',
  'pin_reference_value',
  'simplify_labels',
  'show_micro_step',
  'set_stage',
  'restore_stage',
  'annotate_element',
  'show_overview_inset',
  'attention_guide',
  ...STUDENT_STEERING_ACTIONS,
]);

const LEVEL_ACTIONS = {
  1: new Set(['highlight_elements', 'focus_single_element', ...STUDENT_STEERING_ACTIONS]),
  2: new Set([
    'highlight_elements',
    'dim_irrelevant',
    'focus_single_element',
    'show_value_tags',
    ...STUDENT_STEERING_ACTIONS,
  ]),
  3: new Set([
    'highlight_elements',
    'dim_irrelevant',
    'focus_single_element',
    'bold_axes',
    'show_value_tags',
    'show_grid',
    'show_projection_lines',
    'pin_reference_value',
    'simplify_labels',
    'show_micro_step',
    'annotate_element',
    'attention_guide',
    ...STUDENT_STEERING_ACTIONS,
  ]),
  4: new Set(Array.from(ACTION_ALLOWLIST)),
};

const HIGH_STAGE_JUMP_CHARTS = new Set([
  'tree_diagram',
  'box_plot',
  'venn_diagram',
  'trigonometric_function',
  'quadratic_function',
]);

const OVERLAY_FIRST_CHARTS = new Set([
  'bar_chart',
  'line_chart',
  'pie_chart',
  'scatter_plot',
  'linear_function',
  'proportion',
  'band_graph',
  'dot_plot',
  'exponential_function',
  'histogram',
  'inverse_proportion',
  'logarithmic_function',
]);

const CHART_POLICIES = {
  bar_chart: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'dim_irrelevant', 'show_value_tags'],
    secondaryActions: ['show_projection_lines', 'pin_reference_value'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'low',
  },
  line_chart: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'show_projection_lines', 'show_value_tags'],
    secondaryActions: ['pin_reference_value', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'low',
  },
  pie_chart: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'dim_irrelevant', 'show_value_tags'],
    secondaryActions: ['pin_reference_value'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'low',
  },
  scatter_plot: {
    family: 'statistical',
    preferredActions: ['focus_single_element', 'show_projection_lines', 'show_value_tags'],
    secondaryActions: ['dim_irrelevant', 'show_micro_step'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'low',
  },
  histogram: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'show_value_tags', 'dim_irrelevant'],
    secondaryActions: ['pin_reference_value', 'show_micro_step'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'medium',
  },
  box_plot: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'show_value_tags', 'show_micro_step'],
    secondaryActions: ['pin_reference_value', 'set_stage'],
    avoidFirstChoice: [],
    stageJumpSensitivity: 'high',
  },
  area_chart: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'dim_irrelevant', 'show_value_tags'],
    secondaryActions: ['pin_reference_value', 'show_micro_step'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'medium',
  },
  band_graph: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'dim_irrelevant', 'show_value_tags'],
    secondaryActions: ['pin_reference_value', 'show_micro_step'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'medium',
  },
  dot_plot: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'show_value_tags', 'dim_irrelevant'],
    secondaryActions: ['show_micro_step', 'pin_reference_value'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'medium',
  },
  venn_diagram: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'dim_irrelevant', 'show_value_tags'],
    secondaryActions: ['show_micro_step', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'high',
  },
  tree_diagram: {
    family: 'statistical',
    preferredActions: ['highlight_elements', 'dim_irrelevant', 'show_micro_step'],
    secondaryActions: ['show_value_tags', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'high',
  },
  linear_function: {
    family: 'function',
    preferredActions: ['focus_single_element', 'show_projection_lines', 'show_value_tags'],
    secondaryActions: ['pin_reference_value', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'low',
  },
  quadratic_function: {
    family: 'function',
    preferredActions: ['highlight_elements', 'show_projection_lines', 'show_value_tags'],
    secondaryActions: ['show_micro_step', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'high',
  },
  exponential_function: {
    family: 'function',
    preferredActions: ['highlight_elements', 'show_value_tags'],
    secondaryActions: ['pin_reference_value', 'show_micro_step'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'medium',
  },
  logarithmic_function: {
    family: 'function',
    preferredActions: ['highlight_elements', 'show_value_tags', 'show_micro_step'],
    secondaryActions: ['show_projection_lines', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'medium',
  },
  trigonometric_function: {
    family: 'function',
    preferredActions: ['highlight_elements', 'show_value_tags', 'show_overview_inset'],
    secondaryActions: ['show_projection_lines', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'high',
  },
  inverse_proportion: {
    family: 'function',
    preferredActions: ['focus_single_element', 'show_projection_lines', 'show_value_tags'],
    secondaryActions: ['show_micro_step', 'set_stage'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'medium',
  },
  proportion: {
    family: 'function',
    preferredActions: ['highlight_elements', 'show_projection_lines', 'show_value_tags'],
    secondaryActions: ['pin_reference_value', 'show_micro_step'],
    avoidFirstChoice: ['set_stage'],
    stageJumpSensitivity: 'low',
  },
};

const clampLevel = (value) => Math.min(Math.max(Number(value) || 2, 1), 4);

const normalizeGraphType = (graphType) => String(graphType || '').trim().toLowerCase();

const normalizeActionType = (type) => ACTION_ALIASES[type] || type;

const normalizeEventSource = (eventSource) => {
  if (!eventSource) return EVENT_SOURCES.QA;
  if (Object.values(EVENT_SOURCES).includes(eventSource)) {
    return eventSource;
  }
  return EVENT_SOURCES.QA;
};

const dedupeActions = (actions) => {
  const seen = new Set();
  return actions.filter((action) => {
    const elementKey = Array.isArray(action?.element_ids) ? [...action.element_ids].sort().join('|') : '';
    const viewKey = action?.params?.view_mode || '';
    const key = `${action?.type || ''}::${elementKey}::${viewKey}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const shouldUseProjectionCue = (graphType, actionType) => (
  ['line_chart', 'linear_function', 'trigonometric_function'].includes(graphType)
  && ['highlight_elements', 'focus_single_element'].includes(actionType)
);

const shouldUseValueTags = (graphType, actionType) => (
  ['bar_chart', 'line_chart', 'linear_function', 'trigonometric_function'].includes(graphType)
  && ['highlight_elements', 'focus_single_element'].includes(actionType)
);

const questionMentionsAngle = (questionText) => /角|unit ?circle|単位円|degrees?|radian|周期|振幅/i.test(questionText || '');

export const getChartFamily = (graphType) => {
  const normalized = normalizeGraphType(graphType);
  if (STATISTICAL_CHARTS.has(normalized)) return 'statistical';
  if (FUNCTION_CHARTS.has(normalized)) return 'function';
  return 'generic';
};

export const getChartPolicy = (graphType) => {
  const normalized = normalizeGraphType(graphType);
  return CHART_POLICIES[normalized] || {
    family: getChartFamily(normalized),
    preferredActions: ['highlight_elements', 'show_value_tags'],
    secondaryActions: ['dim_irrelevant'],
    avoidFirstChoice: [],
    stageJumpSensitivity: 'medium',
  };
};

// Upper bound on EXTRA function-graph coordinate labels (the manual slider shares it).
export const FUNCTION_LABEL_BUDGET_MAX = 12;

/**
 * Function-graph coordinate-label budget (the AI default).
 *
 * Returns how many EXTRA sampled coordinate labels a function graph should draw
 * ON TOP of the always-kept keypoint labels (max / min / x-intercept / y-intercept
 * / origin). Higher support level and higher cognitive load → fewer extras, so the
 * curve stays readable for the students most vulnerable to visual clutter. This is
 * only the DEFAULT: the "図の調整 / Adjust graph" panel lets a teacher/student
 * override it, and the keypoint categories are NEVER affected by this number.
 *
 * Cognitive axes are floats 0–3 (activation threshold ≥ 2.0), same convention as
 * computeEffectiveSupportLevel. visuospatial is weighted most because coordinate
 * reading is the exact strain a dense label field imposes.
 *
 * @param {object} cognitiveProfile - { visuospatial, working_memory, load_vulnerability, ... }
 * @param {number} supportLevel - effective support level 1–4 (undefined → moderate default)
 * @returns {number} integer extra-label budget in [0, FUNCTION_LABEL_BUDGET_MAX]
 */
export const computeFunctionLabelBudget = (cognitiveProfile, supportLevel) => {
  const lvl = Number(supportLevel);
  let budget;
  if (lvl === 4) budget = 0;
  else if (lvl === 3) budget = 2;
  else if (lvl === 2) budget = 4;
  else if (lvl === 1) budget = 6;
  else budget = 6; // unknown / not-yet-computed support level → moderate default
  const cd = cognitiveProfile || {};
  const heavy = (key) => Number(cd[key] || 0) >= 2;
  if (heavy('visuospatial')) budget -= 2;       // coordinate reading is the exact strain
  if (heavy('working_memory')) budget -= 1;
  if (heavy('load_vulnerability')) budget -= 1;
  return Math.max(0, Math.min(FUNCTION_LABEL_BUDGET_MAX, budget));
};

export const computeEffectiveSupportLevel = ({
  baseSupportLevel = 2,
  cognitiveDifficulty = {},
  activeQuestion = null,
  recentEvents = [],
  graphType = '',
  hasProblemTextContext = true,
}) => {
  const base = clampLevel(baseSupportLevel);
  const questionText = activeQuestion?.text || '';
  const questionType = activeQuestion?.type || '';
  const family = getChartFamily(graphType);

  const hasEvent = (type) => Array.isArray(recentEvents) && recentEvents.some((event) => event?.type === type);
  const wm = Number(cognitiveDifficulty?.working_memory || 0);
  const visuospatial = Number(cognitiveDifficulty?.visuospatial || 0);
  const crossmodal = Number(cognitiveDifficulty?.crossmodal || 0);
  const loadVulnerability = Number(cognitiveDifficulty?.load_vulnerability || 0);
  const executiveInhibition = Number(cognitiveDifficulty?.executive_inhibition || 0);
  const languageSemantic = Number(cognitiveDifficulty?.language_semantic || 0);

  let bump = 0;
  let triggerAxis = null;
  // Phase A (rev3 M4): WHICH condition fired, kept apart from the axis. Each rule
  // below is the same OR as before, evaluated left to right; the first true
  // operand names the event. `fire` only records attribution for the FIRST axis,
  // exactly like triggerAxis, so level/triggerReason are unchanged.
  let triggerEvent = null;
  const fire = (axis, event) => {
    if (!event) return;
    bump = Math.max(bump, 1);
    if (!triggerAxis) {
      triggerAxis = axis;
      triggerEvent = event;
    }
  };
  const firstOf = (...pairs) => {
    const hit = pairs.find(([cond]) => cond);
    return hit ? hit[1] : null;
  };

  if (wm >= 2) {
    fire('working_memory', firstOf(
      [hasEvent('re_question'), 're_question'],
      [/比較|difference|which|more|less|最大|最小/i.test(questionText), 'question_text_pattern'],
      [/comparison/i.test(questionType), 'question_context'],
    ));
  }

  if (visuospatial >= 2) {
    fire('visuospatial', firstOf(
      [family === 'function', 'chart_family'],
      [/座標|点|軸|x|y|intercept|vertex/i.test(questionText), 'question_text_pattern'],
      [/value_reading|coordinate/.test(questionType), 'question_context'],
    ));
  }

  if (crossmodal >= 2) {
    fire('crossmodal', firstOf(
      [hasProblemTextContext && questionText.length >= 18, 'question_context'],
    ));
  }

  if (loadVulnerability >= 2) {
    fire('load_vulnerability', firstOf([hasEvent('idle'), 'idle']));
  }

  if (executiveInhibition >= 2) {
    fire('executive_inhibition', firstOf([hasEvent('re_click'), 're_click']));
  }

  if (languageSemantic >= 2) {
    fire('language_semantic', firstOf(
      [/凡例|label|legend|単位|meaning|意味/i.test(questionText), 'question_text_pattern'],
      [hasEvent('label_question'), 'label_question'],
    ));
  }

  // Modifier: math_anxiety (Section 10.2)
  // When anxiety is high and student shows signs of stress (idle/anxiety expressions),
  // bump to prefer low-load mode. Does NOT jump to Lv4 — just +1.
  const mathAnxiety = Number(cognitiveDifficulty?.math_anxiety || 0);
  if (mathAnxiety >= 2) {
    fire('math_anxiety', firstOf(
      [hasEvent('idle'), 'idle'],
      [/不会|好难|わからない|難しい|不安|confused|help|むり/i.test(questionText), 'question_text_pattern'],
    ));
  }

  // Modifier: graph_literacy (Section 10.2)
  // When graph reading ability is low and student shows confusion about basic elements,
  // bump to trigger show_micro_step or stage retreat more easily.
  const graphLiteracy = Number(cognitiveDifficulty?.graph_literacy || 0);
  if (graphLiteracy >= 2) {
    fire('graph_literacy', firstOf(
      [hasEvent('re_question'), 're_question'],
      [/どこ|先|最初|読めない|読み方|何|どう.*見|どう.*読/i.test(questionText), 'question_text_pattern'],
    ));
  }

  // 静态 floor: executive_inhibition>=2 的学生需一贯的低 distractor 环境 (baseline, 非 escalation)。
  // floor 经过 level 计算, 不经过 bump → triggerReason 仍只由 bump 驱动 (下一行不变)。
  let floor = base;
  if (executiveInhibition >= 2) {
    floor = Math.max(floor, 2);
  }
  // wm/lv 静态 floor: working_memory>=2 / load_vulnerability>=2 の学生は upload 時に
  // grounded show_value_tags(Lv2 action)を出すため、base-Lv1 でも Lv2 を底支えして
  // sanitize 除去を防ぐ。floor は bump 非経由ゆえ triggerReason は bump 駆動のまま不変 (v2.14.26 契約)。
  if (wm >= 2) {
    floor = Math.max(floor, 2);
  }
  if (loadVulnerability >= 2) {
    floor = Math.max(floor, 2);
  }
  // visuospatial / crossmodal / language_semantic 静态 floor (v2.14.29): これら 3 軸も upload 時に
  // grounded show_value_tags(Lv2 action)を出すため、base-Lv1 でも Lv2 を底支えして sanitize 除去を防ぐ。
  // floor は bump 非経由ゆえ triggerReason は bump 駆動のまま不変 (v2.14.26 契約)。
  // 重要: show_value_tags は LEVEL_ACTIONS[2] のみ。visuospatial の bold_axes/show_grid/
  // show_projection_lines と language_semantic の simplify_labels は LEVEL_ACTIONS[3] ゆえ
  // この Lv2 floor では解禁されない (Lv3 gating 不変 — sanitize guard test で pin)。
  if (visuospatial >= 2) {
    floor = Math.max(floor, 2);
  }
  if (crossmodal >= 2) {
    floor = Math.max(floor, 2);
  }
  if (languageSemantic >= 2) {
    floor = Math.max(floor, 2);
  }
  const level = clampLevel(Math.max(floor, base + bump));
  const triggerReason = bump > 0 ? triggerAxis : null;
  const ruleId = bump > 0 && triggerAxis && triggerEvent
    ? `bump.${triggerAxis}.${triggerEvent}.v1`
    : null;
  // The first static floor (in the order the floors are applied above) that lifts
  // the level above the student's own base, when no bump explains the level.
  let floorRuleId = null;
  if (bump === 0 && floor > base) {
    const floorAxis = [
      ['executive_inhibition', executiveInhibition],
      ['working_memory', wm],
      ['load_vulnerability', loadVulnerability],
      ['visuospatial', visuospatial],
      ['crossmodal', crossmodal],
      ['language_semantic', languageSemantic],
    ].find(([, score]) => score >= 2);
    floorRuleId = floorAxis ? `floor.${floorAxis[0]}.v1` : null;
  }
  return {
    level,
    triggerReason,
    triggerEvent: bump > 0 ? triggerEvent : null,
    ruleId,
    floorRuleId,
  };
};

/**
 * Compute stable UI overlay actions from a cognitive difficulty profile.
 * Client-side mirror of the backend compute_initial_overlays rule; used as a
 * fallback by ProblemIntegrationView when the upload API returns no initial_overlays.
 * Each axis is a float 0-3; threshold for activation is >= 2.0.
 *
 * Relocated from ProblemIntegrationView.js (2026-06-16) so the grounding can be
 * tested in this leaf util without dragging the heavy component tree into Jest.
 * Behaviour is byte-identical to the prior in-component definition.
 *
 * @param {object} cognitiveProfile - { working_memory, visuospatial, executive_inhibition, load_vulnerability, crossmodal, language_semantic, ... }
 * @param {string[]} elementIds - the chart's element ids; grounds show_value_tags so it actually renders (it is element-targeted, not global)
 * @returns {Array<{type: string, element_ids?: string[], params?: object}>}
 */
export const computeStableOverlays = (cognitiveProfile, elementIds = []) => {
  if (!cognitiveProfile) return [];
  const overlays = [];

  // grounded show_value_tags (value externalization). Mirrors the backend
  // compute_initial_overlays rule; element_ids make show_value_tags actually render
  // (it is element-targeted, not global). A' fix 2026-06-15; ALL emitting axes now ground
  // it (visuospatial / wm / lv / crossmodal / language_semantic) — v2.14.29. The former
  // highlight_elements / pin_reference_value / focus_single_element were inert no-ops.
  // Primary axis overlays
  if ((cognitiveProfile.visuospatial ?? 0) >= 2.0) {
    overlays.push({ type: 'bold_axes' });
    overlays.push({ type: 'show_value_tags', element_ids: elementIds });
    overlays.push({ type: 'show_grid' });
  }
  if ((cognitiveProfile.working_memory ?? 0) >= 2.0) {
    overlays.push({ type: 'show_value_tags', element_ids: elementIds });
  }
  if ((cognitiveProfile.load_vulnerability ?? 0) >= 2.0) {
    overlays.push({ type: 'show_value_tags', element_ids: elementIds });
  }

  // Secondary axis overlays
  if ((cognitiveProfile.crossmodal ?? 0) >= 2.0) {
    overlays.push({ type: 'show_value_tags', element_ids: elementIds });
  }
  if ((cognitiveProfile.language_semantic ?? 0) >= 2.0) {
    overlays.push({ type: 'show_value_tags', element_ids: elementIds });
    overlays.push({ type: 'simplify_labels' });
  }

  // Deduplicate by type (keep first; prefer-grounded: upgrade a kept-ungrounded entry to a
  // later grounded variant). Mirrors the backend dedup. A' fix 2026-06-15. After v2.14.29
  // every emitting axis grounds show_value_tags, so the prefer-grounded upgrade is now
  // defensive-only (no ungrounded variant arises from the rule table); kept for safety.
  const seen = new Map();
  const deduped = [];
  overlays.forEach((a) => {
    if (!seen.has(a.type)) {
      seen.set(a.type, deduped.length);
      deduped.push(a);
      return;
    }
    const idx = seen.get(a.type);
    const kept = deduped[idx];
    const keptGrounded = Array.isArray(kept.element_ids) && kept.element_ids.length > 0;
    const incomingGrounded = Array.isArray(a.element_ids) && a.element_ids.length > 0;
    if (!keptGrounded && incomingGrounded) {
      deduped[idx] = a;
    }
  });

  // Priority cap: max 4 actions (primary + secondary axes only)
  return deduped.slice(0, 4);
};

export const sanitizeUiActions = (
  actions,
  {
    graphType = '',
    supportLevel = 2,
    eventSource = EVENT_SOURCES.QA,
    currentQuestionId = null,
    currentQuestionText = '',
    stableLayer = false,
  } = {}
) => {
  if (!Array.isArray(actions) || actions.length === 0) return [];

  const normalizedGraphType = normalizeGraphType(graphType);
  const normalizedSupportLevel = clampLevel(supportLevel);
  const normalizedEventSource = normalizeEventSource(eventSource);
  const chartPolicy = getChartPolicy(normalizedGraphType);
  const allowedForLevel = LEVEL_ACTIONS[normalizedSupportLevel] || LEVEL_ACTIONS[2];
  const sanitized = [];

  actions.forEach((rawAction, index) => {
    if (!rawAction?.type) return;
    const normalizedType = normalizeActionType(rawAction.type);
    if (!ACTION_ALLOWLIST.has(normalizedType)) return;
    if (normalizedEventSource === EVENT_SOURCES.HOVER) return;
    if (!allowedForLevel.has(normalizedType)) return;

    if ((normalizedType === 'set_stage' || normalizedType === 'restore_stage')) {
      if (normalizedSupportLevel < 4) return;
      const stageJumpAllowed =
        HIGH_STAGE_JUMP_CHARTS.has(normalizedGraphType) ||
        chartPolicy.stageJumpSensitivity === 'high' ||
        rawAction?.params?.force_stage === true;
      if (!stageJumpAllowed || OVERLAY_FIRST_CHARTS.has(normalizedGraphType)) {
        return;
      }
    }

    // The stable layer re-applies on EVERY load of the session, so anything
    // that survives here is permanent, not a response to the moment. That is
    // fine for decoration and wrong for the three D5 actions: they exist to
    // carry out a request the student just made, which is by definition a
    // transient/chat-layer event. Delivered as initial_overlays instead, a
    // stage jump would drag the student's chart somewhere they did not ask for
    // every single time they opened the session, and a visibility override
    // would silently outlive the question that motivated it. So D5 is
    // chat-layer only — the stable-layer strip is NOT relaxed for it.
    if (stableLayer && (
      normalizedType === 'attention_guide'
      || normalizedType === 'set_stage'
      || normalizedType === 'restore_stage'
      || STUDENT_STEERING_ACTIONS.includes(normalizedType)
    )) {
      return;
    }

    const action = {
      ...rawAction,
      type: normalizedType,
      event_source: rawAction.event_source || normalizedEventSource,
      replay_group: rawAction.replay_group || (currentQuestionId ? `${normalizedGraphType}:${currentQuestionId}` : `${normalizedGraphType}:global:${index}`),
    };

    if (action.type === 'show_value_tags' && (!Array.isArray(action.element_ids) || action.element_ids.length === 0) && !stableLayer) {
      return;
    }

    if (action.type === 'show_overview_inset' && normalizedGraphType !== 'trigonometric_function') {
      return;
    }

    if (action.type === 'show_overview_inset' && !questionMentionsAngle(currentQuestionText) && normalizedSupportLevel < 4) {
      return;
    }

    if (normalizedSupportLevel < 4) {
      action.loop = false;
      action.loop_until = null;
      action.duration_ms = action.duration_ms || 1200;
    } else {
      action.duration_ms = action.duration_ms || 1600;
    }

    sanitized.push(action);

    if (
      normalizedSupportLevel >= 3
      && Array.isArray(action.element_ids)
      && action.element_ids.length > 0
      && shouldUseProjectionCue(normalizedGraphType, action.type)
    ) {
      sanitized.push({
        type: 'show_projection_lines',
        element_ids: action.element_ids,
        event_source: normalizedEventSource,
        duration_ms: action.duration_ms,
        replay_group: action.replay_group,
      });
    }

    if (
      normalizedSupportLevel >= 3
      && Array.isArray(action.element_ids)
      && action.element_ids.length > 0
      && shouldUseValueTags(normalizedGraphType, action.type)
    ) {
      sanitized.push({
        type: 'show_value_tags',
        element_ids: action.element_ids,
        params: action.params?.pin ? action.params : undefined,
        event_source: normalizedEventSource,
        duration_ms: action.duration_ms,
        replay_group: action.replay_group,
      });
    }

    if (
      normalizedSupportLevel >= 4
      && normalizedGraphType === 'trigonometric_function'
      && questionMentionsAngle(currentQuestionText)
      && ['highlight_elements', 'focus_single_element'].includes(action.type)
    ) {
      sanitized.push({
        type: 'show_overview_inset',
        params: { view_mode: 'unit_circle' },
        event_source: normalizedEventSource,
        duration_ms: action.duration_ms,
        replay_group: action.replay_group,
      });
    }
  });

  return dedupeActions(sanitized);
};

export const splitReplayActions = (
  actions,
  {
    supportLevel = 2,
    eventSource = EVENT_SOURCES.QA,
    isCurrentQuestionContext = false,
  } = {}
) => {
  if (!Array.isArray(actions) || actions.length === 0) {
    return { baseActions: [], replayActions: [] };
  }

  const normalizedSupportLevel = clampLevel(supportLevel);
  const normalizedEventSource = normalizeEventSource(eventSource);
  const replayAllowed =
    normalizedSupportLevel >= 4
    && (
      normalizedEventSource === EVENT_SOURCES.QA
      || (normalizedEventSource === EVENT_SOURCES.CLICK && isCurrentQuestionContext)
    );

  if (!replayAllowed) {
    return { baseActions: actions, replayActions: [] };
  }

  const replayActions = actions
    .filter((action) => REPLAY_ELIGIBLE_ACTION_TYPES.has(action?.type))
    .map((action) => ({
      ...action,
      loop: true,
      loop_until: action.loop_until || 'until_context_change',
      duration_ms: action.duration_ms || 1600,
    }));

  const replayActionKeys = new Set(
    replayActions.map((action) => `${action.type}::${Array.isArray(action.element_ids) ? action.element_ids.join('|') : ''}`)
  );

  const baseActions = actions.filter((action) => {
    const key = `${action?.type}::${Array.isArray(action?.element_ids) ? action.element_ids.join('|') : ''}`;
    return !replayActionKeys.has(key);
  });

  return { baseActions, replayActions };
};

export const buildReplayIntervalMs = (animationSpeed = 1, actions = []) => {
  const speed = Math.min(Math.max(Number(animationSpeed) || 1, 0.5), 2.5);
  const explicit = actions.find((action) => Number.isFinite(action?.duration_ms));
  const baseDuration = explicit?.duration_ms || 1600;
  return Math.max(Math.round(baseDuration / speed), 600);
};

export const isStableChartType = (graphType) => STABLE_CHART_TYPES.includes(normalizeGraphType(graphType));

export const isPrimaryRolloutChart = (graphType) => PRIMARY_ROLLOUT_CHARTS.includes(normalizeGraphType(graphType));
